import { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";

const STORAGE_KEY = "ogs_lang";

const TRANSLATIONS = {
  de: {
    // Header
    "nav.services": "Leistungen",
    "nav.contact": "Kontakt",
    "cta.book": "Termin buchen",

    // Landing – Hero
    "hero.book": "Termin buchen",
    "hero.services": "Leistungen ansehen",

    // Landing – Services section
    "services.tag": "Unsere Leistungen",
    "services.heading": "Alles aus einer Hand",
    "services.subtitle": "Wählen Sie eine Leistung und buchen Sie direkt einen Termin online.",
    "services.from": "ab",
    "services.min": "Min",
    "services.cta": "Jetzt Termin buchen",

    // Landing – CTA
    "cta.ready": "Bereit für den Termin?",
    "cta.readySub": "Online buchen · Bestätigung in Echtzeit",

    // Footer
    "footer.contact": "Kontakt",
    "footer.hours": "Öffnungszeiten",
    "footer.mon_sat": "Mo – Sa",
    "footer.sunday": "Sonntag",
    "footer.impressum": "Impressum",
    "footer.admin": "Admin",

    // Booking Wizard
    "book.tag": "Buchung",
    "book.title": "Werkstatt-Termin",
    "book.subtitle": "In wenigen Schritten zum bestätigten Termin.",
    "book.step.service": "Leistung",
    "book.step.date": "Termin",
    "book.step.vehicle": "Fahrzeug",
    "book.step.contact": "Kontakt",
    "book.step.review": "Bestätigung",
    "book.chooseService": "Leistung wählen",
    "book.dateTime": "Datum & Uhrzeit",
    "book.slots.available": "Verfügbare Zeitfenster",
    "book.slots.pickDate": "Bitte wählen Sie zuerst ein Datum.",
    "book.slots.loading": "Laden...",
    "book.slots.none": "Keine Zeitfenster verfügbar. Bitte anderes Datum wählen.",
    "book.vehicle": "Fahrzeug",
    "book.make": "Marke *",
    "book.model": "Modell *",
    "book.year": "Baujahr *",
    "book.plate": "Kennzeichen *",
    "book.contact": "Kontakt",
    "book.name": "Name *",
    "book.email": "E-Mail *",
    "book.phone": "Telefon *",
    "book.notes": "Bemerkung",
    "book.notesPlaceholder": "Symptome, spezielle Wünsche...",
    "book.review": "Bestätigung",
    "book.row.service": "Leistung",
    "book.row.price": "Preis",
    "book.row.appt": "Termin",
    "book.row.vehicle": "Fahrzeug",
    "book.row.plate": "Kennzeichen",
    "book.row.customer": "Kunde",
    "book.row.email": "E-Mail",
    "book.row.phone": "Telefon",
    "book.row.notes": "Bemerkung",
    "book.row.dateTime": "Datum & Uhrzeit",
    "book.back": "Zurück",
    "book.next": "Weiter",
    "book.submitting": "Wird gebucht...",
    "book.submit": "Termin bestätigen",
    "book.toast.success": "Termin gebucht!",

    // Booking Success
    "success.tag": "Buchung bestätigt",
    "success.title": "Vielen Dank!",
    "success.sent": "Wir haben Ihre Buchung erhalten und eine Bestätigung an",
    "success.sentTo": "gesendet.",
    "success.reference": "Ihre Referenznummer",
    "success.backHome": "Zur Startseite",

    // Impressum
    "imp.tag": "Rechtliches",
    "imp.title": "Impressum",
    "imp.legalRef": "Angaben gemäß § 5 TMG",
    "imp.provider": "Anbieter",
    "imp.contact": "Kontakt",
    "imp.phone": "Telefon:",
    "imp.email": "E-Mail:",
    "imp.ustid": "Umsatzsteuer-ID",
    "imp.register": "Handelsregister / Gewerbe",
    "imp.responsible": "Verantwortlich für den Inhalt",
    "imp.responsibleRef": "(gemäß § 18 Abs. 2 Medienstaatsvertrag)",
    "imp.additional": "Weitere Angaben",
    "imp.liability": "Haftung für Inhalte",
    "imp.liabilityText":
      "Als Diensteanbieter sind wir für eigene Inhalte auf dieser Website nach den allgemeinen Gesetzen gemäß § 7 Abs. 1 TMG verantwortlich. Nach §§ 8 bis 10 TMG sind wir jedoch nicht verpflichtet, übermittelte oder gespeicherte fremde Informationen zu überwachen oder nach Umständen zu forschen, die auf eine rechtswidrige Tätigkeit hinweisen.",
    "imp.dispute": "Streitschlichtung",
    "imp.disputeText1": "Die Europäische Kommission stellt eine Plattform zur Online-Streitbeilegung (OS) bereit:",
    "imp.disputeText2":
      ". Wir sind nicht bereit oder verpflichtet, an Streitbeilegungsverfahren vor einer Verbraucherschlichtungsstelle teilzunehmen.",
    "back": "Zurück",
  },
  en: {
    // Header
    "nav.services": "Services",
    "nav.contact": "Contact",
    "cta.book": "Book now",

    // Landing – Hero
    "hero.book": "Book appointment",
    "hero.services": "View services",

    // Landing – Services section
    "services.tag": "Our services",
    "services.heading": "Everything under one roof",
    "services.subtitle": "Pick a service and book your appointment online.",
    "services.from": "from",
    "services.min": "min",
    "services.cta": "Book now",

    // Landing – CTA
    "cta.ready": "Ready for your appointment?",
    "cta.readySub": "Book online · Instant confirmation",

    // Footer
    "footer.contact": "Contact",
    "footer.hours": "Opening hours",
    "footer.mon_sat": "Mon – Sat",
    "footer.sunday": "Sunday",
    "footer.impressum": "Legal notice",
    "footer.admin": "Admin",

    // Booking Wizard
    "book.tag": "Booking",
    "book.title": "Garage appointment",
    "book.subtitle": "Just a few steps to your confirmed appointment.",
    "book.step.service": "Service",
    "book.step.date": "Date",
    "book.step.vehicle": "Vehicle",
    "book.step.contact": "Contact",
    "book.step.review": "Review",
    "book.chooseService": "Choose service",
    "book.dateTime": "Date & time",
    "book.slots.available": "Available time slots",
    "book.slots.pickDate": "Please pick a date first.",
    "book.slots.loading": "Loading...",
    "book.slots.none": "No time slots available. Please pick another date.",
    "book.vehicle": "Vehicle",
    "book.make": "Make *",
    "book.model": "Model *",
    "book.year": "Year *",
    "book.plate": "License plate *",
    "book.contact": "Contact",
    "book.name": "Name *",
    "book.email": "Email *",
    "book.phone": "Phone *",
    "book.notes": "Notes",
    "book.notesPlaceholder": "Symptoms, special requests...",
    "book.review": "Review",
    "book.row.service": "Service",
    "book.row.price": "Price",
    "book.row.appt": "Appointment",
    "book.row.vehicle": "Vehicle",
    "book.row.plate": "License plate",
    "book.row.customer": "Customer",
    "book.row.email": "Email",
    "book.row.phone": "Phone",
    "book.row.notes": "Notes",
    "book.row.dateTime": "Date & time",
    "book.back": "Back",
    "book.next": "Next",
    "book.submitting": "Booking...",
    "book.submit": "Confirm appointment",
    "book.toast.success": "Appointment booked!",

    // Booking Success
    "success.tag": "Booking confirmed",
    "success.title": "Thank you!",
    "success.sent": "We received your booking and sent a confirmation to",
    "success.sentTo": ".",
    "success.reference": "Your reference number",
    "success.backHome": "Back to home",

    // Impressum
    "imp.tag": "Legal",
    "imp.title": "Legal notice",
    "imp.legalRef": "Information according to § 5 TMG (German law)",
    "imp.provider": "Provider",
    "imp.contact": "Contact",
    "imp.phone": "Phone:",
    "imp.email": "Email:",
    "imp.ustid": "VAT ID",
    "imp.register": "Commercial register / Trade",
    "imp.responsible": "Responsible for content",
    "imp.responsibleRef": "(as per § 18 (2) Medienstaatsvertrag)",
    "imp.additional": "Additional information",
    "imp.liability": "Liability for content",
    "imp.liabilityText":
      "As a service provider, we are responsible for our own content on this website under general laws pursuant to § 7 (1) TMG. However, according to §§ 8 to 10 TMG, we are not obliged to monitor transmitted or stored third-party information or to investigate circumstances that indicate illegal activity.",
    "imp.dispute": "Dispute resolution",
    "imp.disputeText1":
      "The European Commission provides a platform for online dispute resolution (ODR):",
    "imp.disputeText2":
      ". We are neither willing nor obligated to participate in dispute resolution proceedings before a consumer arbitration board.",
    "back": "Back",
  },
};

const LanguageContext = createContext({ lang: "de", setLang: () => {}, t: (k) => k });

export function LanguageProvider({ children }) {
  const [lang, setLangState] = useState(() => {
    try {
      const saved = localStorage.getItem(STORAGE_KEY);
      if (saved === "de" || saved === "en") return saved;
    } catch (_) {}
    return "de";
  });

  const setLang = useCallback((l) => {
    setLangState(l);
    try {
      localStorage.setItem(STORAGE_KEY, l);
    } catch (_) {}
  }, []);

  useEffect(() => {
    document.documentElement.lang = lang;
  }, [lang]);

  const t = useCallback(
    (key) => (TRANSLATIONS[lang] && TRANSLATIONS[lang][key]) || TRANSLATIONS.de[key] || key,
    [lang],
  );

  const value = useMemo(() => ({ lang, setLang, t }), [lang, setLang, t]);
  return <LanguageContext.Provider value={value}>{children}</LanguageContext.Provider>;
}

export function useLanguage() {
  return useContext(LanguageContext);
}

export function useT() {
  return useContext(LanguageContext).t;
}
